import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './goBackModal.css';

const StruggleSupportModal = ({ 
  isVisible, 
  variant, // 'errors' or 'silence'
  onClose, 
  onGoToVocab, 
  onGoToVideo, 
  onContinue 
}) => {
  const { t } = useTranslation();

  const content = {
    errors: {
      mascot: '💪',
      title: t('Take a break and review!'),
      desc: t('You\'ve tried five times already - that\'s great persistence! But it seems like it would be worth refreshing the material so the dialogue goes easier.'),
      dotClass: 'error',
      badge: t('Mistakes are part of learning')
    },
    silence: {
      mascot: '🤗',
      title: t('Nothing scary, it happens!'),
      desc: t('It seems difficult to answer by voice for now. Let\'s first review the material again - after that it will be much easier!'),
      dotClass: 'silent',
      badge: t('One step back is two steps forward')
    }
  };

  const currentContent = content[variant] || content.errors;

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isVisible) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className={`overlay ${isVisible ? 'visible' : ''}`} 
        onClick={onClose}
      />

      {/* Pop-up */}
      <div className={`popup ${isVisible ? 'visible' : ''}`}>
        {/* Header */}
        <div className="popup-header2">
          <div className="popup-mascot">{currentContent.mascot}</div>
          <h2>{currentContent.title}</h2>
          <p>{currentContent.desc}</p>

          {/* Attempt dots */}
          <div className="popup-dots">
            {[0, 1, 2, 3, 4].map((i) => (
              <div 
                key={i} 
                className={`pdot ${currentContent.dotClass}`}
              />
            ))}
          </div>

          {/* Motivational badge */}
          <div className="motivational-badge">
            <span className="badge-icon">⭐</span>
            <span>{currentContent.badge}</span>
          </div>
        </div>

        {/* Body */}
        <div className="popup-body">
          <div className="popup-suggestion">{t('We recommend returning to:')}</div>

          <div className="route-cards">
            {/* Vocabulary route */}
            <div 
              className="route-card vocab" 
              onClick={onGoToVocab}
              role="button" 
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onGoToVocab();
                }
              }}
            >
              <div className="route-icon purple">
                <svg viewBox="0 0 24 24" fill="none" stroke="#6C5CE7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="18" rx="3"/>
                  <path d="M7 8h10"/>
                  <path d="M7 12h6"/>
                  <path d="M7 16h8"/>
                </svg>
              </div>
              <div className="route-info">
                <span className="route-title">{t('Learn words2')}</span>
                <span className="route-desc">{t('Review key words and phrases from the lesson')}</span>
              </div>
            </div>

            {/* Video route */}
            <div 
              className="route-card video" 
              onClick={onGoToVideo}
              role="button" 
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onGoToVideo();
                }
              }}
            >
              <div className="route-icon orange">
                <svg viewBox="0 0 24 24" fill="none" stroke="#FF6B2C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="3"/>
                  <polygon points="10,9 16,12 10,15" fill="#FF6B2C" stroke="none"/>
                </svg>
              </div>
              <div className="route-info">
                <span className="route-title">{t('Watch Video2')}</span>
                <span className="route-desc">{t('Watch the dialogue again with subtitles')}</span>
              </div>
            </div>
          </div>

          {/* Continue anyway */}
          <div className="continue-anyway">
            <button onClick={onContinue}>
              {t('I want to continue the dialogue')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default StruggleSupportModal;
